import { useEffect, useState } from 'react';
// import IdleTimer from 'react-idle-timer';
import { connect } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { ONE_SECOND_DELAY, errorMessages } from '../../constants';
import HttpError from '../../errors/HttpError';
import { refreshAccessTokenAsync } from '../../redux/User/User.actions';
import { selectLoggedInUser } from '../../redux/User/User.selector';
import SessionTimeoutModal from './SessionTimeoutModal.utils';
import { getAccessToken, getRemainingSessionMs } from './UserSession.utils';

let countdownInterval;
let warningTimeout;

const WARNING_WINDOW_MS = 600 * ONE_SECOND_DELAY;

function SessionTimeout({ loggedInUser, logOut, refreshAccessToken }) {
	const [timeoutModalOpen, setTimeoutModalOpen] = useState(false);
	const [timeoutCountdown, setTimeoutCountdown] = useState(0);

	const location = useLocation(); // updates on route change

	const clearSessionTimeout = () => clearTimeout(warningTimeout);
	const clearSessionInterval = () => clearInterval(countdownInterval);

	const clearAllTimers = () => {
		clearSessionInterval();
		clearSessionTimeout();
	};

	const handleLogout = async () => {
		try {
			setTimeoutModalOpen(false);
			clearAllTimers();
			logOut();
		} catch (err) {
			throw new HttpError(0, errorMessages.UNDEFINED_RESPONSE);
		}
	};

	const startCountdown = startFromSeconds => {
		let countDown = startFromSeconds;
		setTimeoutModalOpen(true);
		setTimeoutCountdown(countDown);

		countdownInterval = setInterval(() => {
			countDown -= 1;
			if (countDown > 0) {
				setTimeoutCountdown(countDown);
			} else {
				clearAllTimers();
				handleLogout();
			}
		}, ONE_SECOND_DELAY);
	};

	const scheduleExpiryWarning = () => {
		clearAllTimers();
		if (!loggedInUser) return;

		// Always fetch latest token from storage (important after refresh)
		const token = getAccessToken();
		const remainingMs = getRemainingSessionMs(token);

		if (remainingMs <= 0) {
			handleLogout();
			return;
		}

		// If already in warning window, show immediately
		if (remainingMs <= WARNING_WINDOW_MS) {
			startCountdown(Math.floor(remainingMs / 1000));
			return;
		}

		// Schedule modal for last 10 seconds before actual token expiry
		const timeUntilWarning = Math.max(remainingMs - 10000, 0);
		warningTimeout = setTimeout(() => {
			startCountdown(10);
		}, timeUntilWarning);
	};

	const handleContinue = async () => {
		try {
			setTimeoutModalOpen(false);
			clearAllTimers();
			// Use mapped redux action (thunk), not service direct call
			await refreshAccessToken();

			// Recompute timers based on refreshed token
			scheduleExpiryWarning();
		} catch (err) {
			// On refresh failure force logout + redirect
			handleLogout();
		}
	};

	// const onActive = () => {};
	// const onIdle = () => {};

	useEffect(() => {
		if (loggedInUser) {
			scheduleExpiryWarning();
		}
		return () => clearAllTimers();
	}, [loggedInUser]);

	useEffect(() => {
		const url = location.pathname;
		if (url.toLowerCase().includes('login')) {
			setTimeoutModalOpen(false);
			clearAllTimers();
		}
	}, [location]);

	return (
		<>
			{/* <IdleTimer
				ref={idleTimer}
				onActive={onActive}
				onIdle={onIdle}
				debounce={250}
				timeout={24 * 60 * 60 * 1000} // token expiry is the primary control
			/> */}
			<SessionTimeoutModal
				countdown={timeoutCountdown}
				onContinue={handleContinue}
				onLogout={handleLogout}
				open={timeoutModalOpen}
			/>
		</>
	);
}

const mapStateToProps = createStructuredSelector({
	loggedInUser: selectLoggedInUser,
});

// No mapStateToProps since isAuthenticated and logOut come from parent
const mapDispatchToProps = dispatch => ({
	refreshAccessToken: () => dispatch(refreshAccessTokenAsync()),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SessionTimeout),
);
